/**
 * DeterminedPropertiesGrid — brick/grid of determined properties (§13.1 / DM-26–28).
 *
 * Block M1b (TZ 2026-07-29). Replaces M1a-FE `PropertiesStub`. Reads
 * property keys + presence counts from the dataset-wide `properties_meta`
 * (writer mirrors full state per row, so we pick the first row's payload).
 *
 * - bold name (§13.1)
 * - italic "Multiple values" checkbox (§13.2 / DM-27)
 * - delete × / trash on the right (§13.5 / DM-28)
 * - i-tooltip with status counts + source breakdown
 * - compact responsive: 1 col on mobile, 2 col @640, 3 col @1024
 *
 * Source-of-truth note: writer mirrors full `properties_meta` onto every
 * DatasetRow.mapped_data. Picking the first row is the same convention used
 * by AggregateChips.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Trash2 } from 'lucide-react';

import {
  useDeleteProperty,
  usePatchProperty,
} from '@/hooks/useMatching';
import { useMatchingStore } from '@/store/matchingStore';
import { usePipelineMatchingRows } from '@/hooks/useMatching';
import { PropertyDeleteModal } from './PropertyDeleteModal';
import { cn } from '@/lib/utils';

// === Types (mirror BE MatchingDeterminedProperty + MatchingPropertiesMeta) ===

export interface DeterminedPropertySummary {
  property_key: string;
  name: string;
  presence: number;        // total rows that have this property extracted
  multivalue: boolean;     // per-property flag
  presence_per_status: { matched?: number; low_confidence?: number; unresolved?: number };
  // Optional: per-status presence counts for the i-tooltip
}

export interface DeterminedPropertiesGridProps {
  pipelineId: string | null;
}

export function DeterminedPropertiesGrid({ pipelineId }: DeterminedPropertiesGridProps) {
  const { t } = useTranslation();
  const { data: rowsResp } = usePipelineMatchingRows(pipelineId);
  const deleteProperty = useDeleteProperty();
  const patchProperty = usePatchProperty();
  const propertyOverrides = useMatchingStore((s) => s.propertyMultivalueOverrides);
  const setPropertyMultivalue = useMatchingStore((s) => s.setPropertyMultivalue);

  const [pendingDelete, setPendingDelete] = useState<DeterminedPropertySummary | null>(null);

  // === Read properties_meta (dataset-wide, mirrored per row) ===
  const properties: DeterminedPropertySummary[] = useMemo(() => {
    const rows = rowsResp?.rows ?? [];
    const firstWithMeta = rows.find(
      (r) =>
        r.mapped_data &&
        typeof (r.mapped_data as { properties_meta?: object }).properties_meta === 'object',
    );
    const meta = (firstWithMeta?.mapped_data as {
      properties_meta?: {
        multivalue?: Record<string, boolean>;
        presence?: Record<string, number>;
        presence_per_status?: Record<string, Record<string, number>>;
      };
    } | null | undefined)?.properties_meta;
    if (!meta) return [];

    // Pull a sample row to read the human-readable `name` from MatchingDeterminedProperty.shape.
    // (writer mirrors full state, so each row's row entry has properties[property_key].name.)
    const sampleRow = rows.find(
      (r) =>
        r.mapped_data &&
        typeof (r.mapped_data as { rows?: object }).rows === 'object',
    );
    const rowsDict = (sampleRow?.mapped_data as {
      rows?: Record<
        string,
        {
          properties?: Record<
            string,
            { name?: string }
          >;
        }
      >;
    } | null | undefined)?.rows;
    const nameMap: Record<string, string> = {};
    if (rowsDict) {
      for (const entry of Object.values(rowsDict)) {
        const props = entry.properties || {};
        for (const [k, v] of Object.entries(props)) {
          if (v?.name && !nameMap[k]) nameMap[k] = v.name;
        }
      }
    }

    const out: DeterminedPropertySummary[] = [];
    for (const [k, presence] of Object.entries(meta.presence ?? {})) {
      out.push({
        property_key: k,
        name: nameMap[k] ?? k,
        presence,
        multivalue: meta.multivalue?.[k] ?? false,
        presence_per_status: meta.presence_per_status?.[k] ?? {},
      });
    }
    // Property-delimiter from meta (single source of truth)
    return out.sort((a, b) => b.presence - a.presence);
  }, [rowsResp]);

  const delimiterFromMeta = useMemo(() => {
    const rows = rowsResp?.rows ?? [];
    const firstWithMeta = rows.find(
      (r) =>
        r.mapped_data &&
        typeof (r.mapped_data as { properties_meta?: object }).properties_meta === 'object',
    );
    return (firstWithMeta?.mapped_data as { properties_meta?: { output_delimiter?: string } } | null | undefined)
      ?.properties_meta?.output_delimiter ?? ',';
  }, [rowsResp]);

  if (!pipelineId || properties.length === 0) return null;

  function handleMultivalueToggle(p: DeterminedPropertySummary, on: boolean) {
    if (!pipelineId) return;
    setPropertyMultivalue(p.property_key, on);
    patchProperty.mutate({
      pipeline_id: pipelineId,
      property_key: p.property_key,
      multivalue: on,
    });
  }

  function handleConfirmDelete() {
    if (!pendingDelete || !pipelineId) return;
    const key = pendingDelete.property_key;
    deleteProperty.mutate(
      { pipeline_id: pipelineId, property_key: key },
      { onSettled: () => setPendingDelete(null) },
    );
  }

  return (
    <section className="card space-y-3" data-testid="determined-properties-grid">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400">
            {t('process.matching.properties.title', {
              defaultValue: 'Determined properties',
            })}
          </h2>
          <p className="text-xs text-surface-500">
            {t('process.matching.properties.subtitle', {
              defaultValue:
                'Values extracted from the selected template and manual cards. {n} properties found.',
              n: properties.length,
            })}
          </p>
        </div>
        <div className="text-xs text-surface-400">
          {t('process.matching.properties.delimiter', {
            defaultValue: 'Output delimiter: {d}',
            d: delimiterFromMeta === ',' ? ',' : delimiterFromMeta,
          })}
        </div>
      </header>

      <div
        className={cn(
          'grid gap-2',
          'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {properties.map((p) => {
          const statusBreakdown = p.presence_per_status;
          const matched = statusBreakdown.matched ?? 0;
          const low = statusBreakdown.low_confidence ?? 0;
          const unresolved = statusBreakdown.unresolved ?? 0;
          // Worst band across counts (honest display): red if any unresolved > 0
          // else orange if any low > 0 else green.
          const band: 'green' | 'orange' | 'red' = unresolved > 0
            ? 'red'
            : low > 0
            ? 'orange'
            : 'green';
          return (
            <div
              key={p.property_key}
              data-testid="determined-property-cell"
              data-property-key={p.property_key}
              className={cn(
                'rounded-lg border p-3 flex flex-col gap-2',
                band === 'green' && 'border-success-tint/40 bg-success-tint/10',
                band === 'orange' && 'border-warning-tint/40 bg-warning-tint/10',
                band === 'red' && 'border-danger-tint/40 bg-danger-tint/10',
              )}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-surface-100 truncate" title={p.name}>
                    {p.name}
                  </div>
                  <div className="text-[11px] text-surface-500 font-mono truncate" title={p.property_key}>
                    {p.property_key}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPendingDelete(p)}
                  disabled={deleteProperty.isPending}
                  className="p-1 rounded hover:bg-danger-tint/30 text-danger transition-colors disabled:opacity-50"
                  title={t('process.matching.properties.delete', {
                    defaultValue: 'Remove property',
                  })}
                  aria-label={t('process.matching.properties.delete', {
                    defaultValue: 'Remove property',
                  })}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="flex items-center justify-between text-xs">
                <label className="flex items-center gap-2 italic text-surface-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={propertyOverrides[p.property_key] ?? p.multivalue}
                    onChange={(e) =>
                      handleMultivalueToggle(p, e.currentTarget.checked)
                    }
                    disabled={patchProperty.isPending}
                    className="w-3.5 h-3.5"
                    data-testid="multivalue-toggle"
                  />
                  {t('process.matching.properties.multivalue', {
                    defaultValue: 'Multiple values',
                  })}
                </label>
                <div
                  className={cn(
                    'group relative flex items-center gap-1 text-surface-400',
                  )}
                >
                  <span>{p.presence}</span>
                  <Info className="w-3 h-3" />
                  <div className="absolute right-0 top-full mt-1 z-10 hidden group-hover:block w-44 rounded-md bg-surface-900 border border-surface-700 p-2 text-[11px] text-surface-200 shadow-xl">
                    <div>{t('process.matching.properties.tooltip.matched', { defaultValue: 'Matched: {n}', n: matched })}</div>
                    <div>{t('process.matching.properties.tooltip.low', { defaultValue: 'Low conf: {n}', n: low })}</div>
                    <div>{t('process.matching.properties.tooltip.unresolved', { defaultValue: 'Unresolved: {n}', n: unresolved })}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <PropertyDeleteModal
        property={pendingDelete}
        pipelineId={pipelineId}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        busy={deleteProperty.isPending}
      />
    </section>
  );
}
